/**
 * @voltxampere/engine — LED DC stamp (Sprint 23 B1)
 *
 * LED, Shockley junction'ın üstüne LED-spesifik default'lar
 * (Is=1e-12, n=2) bindiren bir wrapper'dır. Stamp matematiksel
 * olarak diode stamp'ıyla aynı (Newton-Raphson companion model);
 * tek fark, parametre fallback default'ları LED için ayarlanmış —
 * `getNumericParam(component, "saturationCurrent", 1e-14)` Si
 * diyot için doğru ama 1.8-2.0 V Vf kırmızı LED için yanlış.
 *
 * Strateji: `component.params`'ı LED-default ön-sıralamasıyla
 * zenginleştir, sonra mevcut diode stamp'ını çağır. Junction-
 * limiting (`pnjlim`) + Newton iter history (`junctionHistory`)
 * + zero-default cap helpers diode stamp'ından otomatik miras alır.
 *
 * Sprint 14.0.1'deki `SCHEMA_SYMBOL_ALIASES.led = "diode"` mapping'i
 * Sprint 23 B1'de KALDIRILIR — LED kendi dedicated stamp definition'a
 * sahip, alias gereksizdir.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { defineDCStamp } from "../definition.js";
import { getNumericParam } from "../terminal-utils.js";
import { diodeDCStamp } from "./diode.js";
/**
 * LED-spesifik Shockley parametre default'ları. Kırmızı LED
 * (Vf ≈ 1.8 V at 15 mA) için empirical fit:
 *
 *   I_D = Is · (exp(V/(n·Vt)) - 1)
 *   0.015 ≈ 1e-12 · exp(1.8/(3·0.0259)) = 1e-12 · exp(23.16) ≈ 11.5 mA
 *
 * 5 V supply + 220 Ω serisinde KCL çözümü Vf ≈ 1.82 V verir;
 * SPRINT_23 B1 fixture testi bu çözüme bant olarak yakındır.
 *
 * n=3 / Is=1e-12 kırmızı LED için makul, yeşil LED n≈2.5, mavi
 * LED n≈4'e kadar çıkar.
 */
const LED_DEFAULT_IS = 1e-12;
const LED_DEFAULT_N = 3;
export const ledDCStamp = (ctx, component) => {
    // LED-specific default'ları açıkça parametrelere enjekte et;
    // diode stamp `getNumericParam(component, "saturationCurrent",
    // 1e-14)` çağırınca bizim önceden yazdığımız değer dönecek.
    const enrichedParams = {
        ...component.params,
        saturationCurrent: getNumericParam(component, "saturationCurrent", LED_DEFAULT_IS),
        ideality: getNumericParam(component, "ideality", LED_DEFAULT_N),
    };
    diodeDCStamp(ctx, { ...component, params: enrichedParams });
};
/**
 * LED diyot junction'ı taşır → anode + cathode junction-bearing.
 * Diode ile aynı topology — Sprint 8a step-size damping LED için
 * de aktiftir.
 */
export const ledDCStampDefinition = defineDCStamp("led", ledDCStamp, [], ["anode", "cathode"]);
//# sourceMappingURL=led.js.map